import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Ring } from '../components/charts.tsx';
import { Reveal } from '../components/motion.tsx';
import { Icon } from '../components/Icon.tsx';
import { Button, Callout, Chip, Empty, Field, PageHead, Panel, Segmented, StatusChip, useToast } from '../components/ui.tsx';
import { ApiError, get, post, sendForm, upload } from '../lib/api.ts';
import { DOCUMENT_HINT, DOCUMENT_TYPES, documentProblem } from '../lib/documents.ts';
import { date, dateRange, todayIn } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';
import { TimesheetView } from './Timesheet.tsx';

interface LeaveType {
  id: string;
  code: string;
  name: string;
  paidByDefault: boolean;
  countsAgainstAnnual: boolean;
  requiresAttachment: boolean;
}
interface Mine {
  leave: { id: string; type: string; fromDate: string; toDate: string; days: number; status: string; reason: string | null; hasDocument: boolean; needsDocument: boolean }[];
  overtime: { id: string; date: string; fromTime: string; toTime: string; status: string; reason: string }[];
  corrections: { id: string; workDate: string; type: string; status: string; notes: string | null }[];
}

export function MyTime() {
  const me = useMe().data!;
  const qc = useQueryClient();
  const toast = useToast();
  const types = useQuery({ queryKey: ['leave-types'], queryFn: () => get<LeaveType[]>('/api/requests/leave-types') });
  const balance = useQuery({ queryKey: ['leave-balance'], queryFn: () => get<{ remaining: number; entitlement: number; used: number; pending: number; periodFrom: string; periodTo: string; counting: string }>('/api/requests/leave/balance') });
  const mine = useQuery({ queryKey: ['my-requests'], queryFn: () => get<Mine>('/api/requests/mine') });
  const [kind, setKind] = useState<'leave' | 'overtime' | 'correction'>('leave');
  const today = todayIn(me.employee?.timeZone ?? undefined);
  const [f, setF] = useState({ leaveTypeId: '', fromDate: today, toDate: today, reason: '', date: today, fromTime: '17:30', toTime: '19:30', workDate: today, type: 'missed_punch', clockIn: '', clockOut: '', permitFrom: '', permitTo: '' });
  const [doc, setDoc] = useState<File | null>(null);
  // Remounts the file input to clear it after a request is sent.
  const [docInput, setDocInput] = useState(0);
  const docProblem = documentProblem(doc);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF((x) => ({ ...x, [k]: e.target.value }));

  const m = useMutation({
    mutationFn: () => {
      if (kind === 'leave') {
        const fields = { leaveTypeId: f.leaveTypeId || types.data?.[0]?.id, fromDate: f.fromDate, toDate: f.toDate, reason: f.reason || undefined };
        return doc ? sendForm('/api/requests/leave', { ...fields, file: doc }) : post('/api/requests/leave', fields);
      }
      if (kind === 'overtime') return post('/api/requests/overtime', { date: f.date, fromTime: f.fromTime, toTime: f.toTime, reason: f.reason });
      return post('/api/requests/corrections', {
        workDate: f.workDate,
        type: f.type,
        clockIn: f.clockIn || undefined,
        clockOut: f.clockOut || undefined,
        permitFrom: f.permitFrom || undefined,
        permitTo: f.permitTo || undefined,
        notes: f.reason || undefined,
        paid: f.type !== 'short_leave',
      });
    },
    onSuccess: () => {
      toast({ title: 'Request sent', body: 'Your manager has been notified.' });
      setF((x) => ({ ...x, reason: '' }));
      setDoc(null);
      setDocInput((n) => n + 1);
      void qc.invalidateQueries({ queryKey: ['my-requests'] });
      void qc.invalidateQueries({ queryKey: ['leave-balance'] });
    },
  });

  const chosen = types.data?.find((t) => t.id === (f.leaveTypeId || types.data?.[0]?.id));
  const span = Math.max(0, (Date.parse(f.toDate) - Date.parse(f.fromDate)) / 86_400_000 + 1);
  const overdraw = chosen?.countsAgainstAnnual && balance.data && span > balance.data.remaining;

  if (!me.employee) return <Empty title="No employee profile" />;
  return (
    <div>
      <PageHead
        eyebrow="My time & leave"
        title={
          <>
            Your hours, <em>your time off.</em>
          </>
        }
      />
      <Reveal className="grid-12">
        <Reveal.Item className="col-4">
          <Panel eyebrow={balance.data ? dateRange(balance.data.periodFrom, balance.data.periodTo) : 'Annual leave'} title="Annual leave">
            <div className="row" style={{ ['--gap' as string]: '24px' }}>
              <Ring value={balance.data?.remaining ?? 0} max={balance.data?.entitlement ?? 12} label="Days remaining" sublabel="days left" />
              <dl className="stack" style={{ ['--gap' as string]: '6px', fontSize: 'var(--step--1)' }}>
                <div>
                  <span className="faint">Entitled</span> {balance.data?.entitlement}
                </div>
                <div>
                  <span className="faint">Taken</span> {balance.data?.used}
                </div>
                <div>
                  <span className="faint">Pending</span> {balance.data?.pending}
                </div>
              </dl>
            </div>
            {balance.data?.counting === 'calendar' ? (
              <p className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 14 }}>
                Leave is counted in calendar days, including weekends inside the range.
              </p>
            ) : null}
          </Panel>
        </Reveal.Item>
        <Reveal.Item className="col-8">
          <Panel
            eyebrow="New request"
            title="Ask for time"
            action={
              <Segmented
                label="Request type"
                idPrefix="req"
                value={kind}
                onChange={setKind}
                options={[
                  { id: 'leave', label: 'Leave' },
                  { id: 'overtime', label: 'Overtime' },
                  { id: 'correction', label: 'Correction' },
                ]}
              />
            }
          >
            <div className="grid-12" style={{ gap: 14 }}>
              {kind === 'leave' ? (
                <>
                  <div className="col-4">
                    <Field label="Type">
                      {(p) => (
                        <select {...p} className="select" value={f.leaveTypeId || types.data?.[0]?.id} onChange={set('leaveTypeId')}>
                          {types.data?.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </Field>
                  </div>
                  <div className="col-4">
                    <Field label="From">{(p) => <input {...p} className="input" type="date" value={f.fromDate} onChange={set('fromDate')} />}</Field>
                  </div>
                  <div className="col-4">
                    <Field label="To">{(p) => <input {...p} className="input" type="date" value={f.toDate} min={f.fromDate} onChange={set('toDate')} />}</Field>
                  </div>
                  <div className="col-12">
                    <Field
                      label={chosen?.requiresAttachment ? 'Doctor’s letter' : 'Supporting document'}
                      aside={chosen?.requiresAttachment ? null : <span className="faint">Optional</span>}
                      hint={chosen?.requiresAttachment ? `${chosen.name} needs a doctor’s letter. No letter yet? Send the request now and add it from My requests below. ${DOCUMENT_HINT}.` : `A doctor’s letter, an invitation, a certificate… ${DOCUMENT_HINT}.`}
                      error={docProblem}
                    >
                      {(p) => <input {...p} key={docInput} className="input" type="file" accept={DOCUMENT_TYPES} onChange={(e) => setDoc(e.target.files?.[0] ?? null)} />}
                    </Field>
                  </div>
                </>
              ) : kind === 'overtime' ? (
                <>
                  <div className="col-4">
                    <Field label="Date">{(p) => <input {...p} className="input" type="date" value={f.date} onChange={set('date')} />}</Field>
                  </div>
                  <div className="col-4">
                    <Field label="From">{(p) => <input {...p} className="input" type="time" value={f.fromTime} onChange={set('fromTime')} />}</Field>
                  </div>
                  <div className="col-4">
                    <Field label="To">{(p) => <input {...p} className="input" type="time" value={f.toTime} onChange={set('toTime')} />}</Field>
                  </div>
                </>
              ) : (
                <>
                  <div className="col-4">
                    <Field label="Day">{(p) => <input {...p} className="input" type="date" value={f.workDate} max={today} onChange={set('workDate')} />}</Field>
                  </div>
                  <div className="col-8">
                    <Field label="What happened">
                      {(p) => (
                        <select {...p} className="select" value={f.type} onChange={set('type')}>
                          <option value="missed_punch">I forgot to clock in or out</option>
                          <option value="late_in">I was late with a good reason</option>
                          <option value="early_out">I had to leave early</option>
                          <option value="short_leave">I stepped out for a while (short leave)</option>
                        </select>
                      )}
                    </Field>
                  </div>
                  {f.type === 'missed_punch' ? (
                    <>
                      <div className="col-6">
                        <Field label="Actual clock-in" hint="Leave blank if it was recorded">{(p) => <input {...p} className="input" type="time" value={f.clockIn} onChange={set('clockIn')} />}</Field>
                      </div>
                      <div className="col-6">
                        <Field label="Actual clock-out" hint="Leave blank if it was recorded">{(p) => <input {...p} className="input" type="time" value={f.clockOut} onChange={set('clockOut')} />}</Field>
                      </div>
                    </>
                  ) : f.type === 'short_leave' ? (
                    <>
                      <div className="col-6">
                        <Field label="Out from">{(p) => <input {...p} className="input" type="time" value={f.permitFrom} onChange={set('permitFrom')} />}</Field>
                      </div>
                      <div className="col-6">
                        <Field label="Back at">{(p) => <input {...p} className="input" type="time" value={f.permitTo} onChange={set('permitTo')} />}</Field>
                      </div>
                    </>
                  ) : null}
                </>
              )}
              <div className="col-12">
                <Field label={kind === 'overtime' ? 'What was the work?' : 'Notes for your manager'}>{(p) => <textarea {...p} className="textarea" style={{ minHeight: 72 }} value={f.reason} onChange={set('reason')} />}</Field>
              </div>
              {overdraw ? (
                <div className="col-12">
                  <Callout>This is {span} days but you have {balance.data!.remaining} left. Your manager will see that before deciding.</Callout>
                </div>
              ) : null}
              {m.error ? (
                <div className="col-12">
                  <Callout tone="bad">{m.error instanceof ApiError ? m.error.message : 'Couldn’t send.'}</Callout>
                </div>
              ) : null}
              <div className="col-12 row">
                <Button variant="primary" icon="arrowRight" loading={m.isPending} onClick={() => m.mutate()} disabled={(kind === 'overtime' && f.reason.trim().length < 3) || (kind === 'leave' && !!docProblem)}>
                  Send request
                </Button>
              </div>
            </div>
          </Panel>
        </Reveal.Item>
        <Reveal.Item className="col-12">
          <Panel eyebrow="History" title="My requests">
            {!mine.data || (!mine.data.leave.length && !mine.data.overtime.length && !mine.data.corrections.length) ? (
              <p className="faint">Nothing yet.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Request</th>
                      <th>When</th>
                      <th>Details</th>
                      <th className="right">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mine.data.leave.map((l) => (
                      <tr key={l.id}>
                        <td>{l.type}</td>
                        <td className="mono">{dateRange(l.fromDate, l.toDate)}</td>
                        <td className="muted">
                          <div className="row" style={{ ['--gap' as string]: '8px', flexWrap: 'wrap' }}>
                            <span>{l.days} day{l.days === 1 ? '' : 's'} {l.reason ? `· ${l.reason}` : ''}</span>
                            <LeaveDocument leave={l} />
                          </div>
                        </td>
                        <td className="right"><StatusChip status={l.status} /></td>
                      </tr>
                    ))}
                    {mine.data.overtime.map((o) => (
                      <tr key={o.id}>
                        <td>Overtime</td>
                        <td className="mono">{date(o.date)}</td>
                        <td className="muted">{o.fromTime.slice(0, 5)}–{o.toTime.slice(0, 5)} · {o.reason}</td>
                        <td className="right"><StatusChip status={o.status} /></td>
                      </tr>
                    ))}
                    {mine.data.corrections.map((c) => (
                      <tr key={c.id}>
                        <td>Correction</td>
                        <td className="mono">{date(c.workDate)}</td>
                        <td className="muted">{c.type.replace('_', ' ')} {c.notes ? `· ${c.notes}` : ''}</td>
                        <td className="right"><StatusChip status={c.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </Reveal.Item>
        <Reveal.Item className="col-12" style={{ marginTop: 'var(--s-5)' }}>
          <TimesheetView employeeId={me.employee.id} embedded />
        </Reveal.Item>
      </Reveal>
    </div>
  );
}

/** The document on one of my leave requests: a link to it, or a way to add it while the request is open. */
function LeaveDocument({ leave }: { leave: Mine['leave'][number] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const add = useMutation({
    mutationFn: (file: File) => upload(`/api/requests/leave/${leave.id}/attachment`, file),
    onSuccess: () => {
      toast({ title: 'Document added' });
      void qc.invalidateQueries({ queryKey: ['my-requests'] });
    },
    onError: (e) => toast({ title: 'Couldn’t add the document', body: e instanceof ApiError ? e.message : undefined, tone: 'error' }),
  });
  const pick = (file: File | undefined) => {
    if (input.current) input.current.value = '';
    if (!file) return;
    const problem = documentProblem(file);
    if (problem) toast({ title: 'Couldn’t add the document', body: problem, tone: 'error' });
    else add.mutate(file);
  };

  if (leave.hasDocument) {
    return (
      <a className="chip chip--file" href={`/api/requests/leave/${leave.id}/attachment`} target="_blank" rel="noopener">
        <Icon name="paperclip" width={13} height={13} /> Document
      </a>
    );
  }
  // Open requests take a document; approved ones only the letter they are missing.
  if (leave.status !== 'pending' && !(leave.status === 'approved' && leave.needsDocument)) return null;
  return (
    <>
      {leave.needsDocument ? <Chip tone="warn">Letter missing</Chip> : null}
      <input ref={input} type="file" accept={DOCUMENT_TYPES} className="visually-hidden" tabIndex={-1} aria-hidden onChange={(e) => pick(e.target.files?.[0])} />
      <Button size="sm" variant="ghost" icon="paperclip" loading={add.isPending} onClick={() => input.current?.click()}>
        Add document
      </Button>
    </>
  );
}
